/**
 * CohortRetentionTable — Day-N return rates per signup-week cohort,
 * mounted at the bottom of the Admin → Health tab (iter197).
 *
 * Lets ops actually MEASURE the iter192–iter196 retention overhaul —
 * line up cohorts that signed up pre-Feb-19-2026 vs post-Feb-19-2026
 * and watch the rates lift.
 *
 * Visual recipe: dark slate table with conditional cell tints — green
 * intensifies as rate ↑, the average row at the bottom is sticky-styled
 * with a thin top border so the eye lands on it immediately.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, TrendingUp, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGE_OPTIONS = [
  { value: 8,  label: '8 weeks' },
  { value: 12, label: '12 weeks' },
  { value: 26, label: '26 weeks' },
];

// Pure-function tint so the table cells visually "heatmap" without a chart.
const tintFor = (rate) => {
  if (rate == null || rate === 0) return { bg: 'rgba(148, 163, 184, 0.08)', fg: '#94A3B8' };
  // Linear ramp from soft slate (0.0) → vivid emerald (0.6+).
  const t = Math.min(1, rate / 0.6);
  // Backgrounds blend slate→emerald, fg stays high-contrast.
  const r = Math.round(148 + (16 - 148) * t);
  const g = Math.round(163 + (185 - 163) * t);
  const b = Math.round(184 + (129 - 184) * t);
  return {
    bg: `rgba(${r}, ${g}, ${b}, ${0.12 + t * 0.18})`,
    fg: t > 0.4 ? '#A7F3D0' : '#E2E8F0',
  };
};

const Pct = ({ rate, n }) => {
  const tint = tintFor(rate);
  if (rate == null) return <span className="text-[var(--isp-muted)]">—</span>;
  return (
    <span
      className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold"
      style={{ background: tint.bg, color: tint.fg }}
      title={`${n} of cohort`}
    >
      {(rate * 100).toFixed(0)}%
      <span className="text-[10px] opacity-60 font-normal">{n}</span>
    </span>
  );
};

const CohortRetentionTable = () => {
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/cohort-retention?weeks=${weeks}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [weeks]);

  return (
    <div
      className="border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl p-5 mt-6"
      data-testid="cohort-retention-table"
    >
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-start gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-4.5 h-4.5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">Day-N retention by cohort</h3>
            <p className="text-xs text-[var(--isp-muted)] mt-0.5 max-w-lg">
              Per-week signup cohorts × Day-1 / 2 / 7 / 30 return rates.
              Use this to compare pre-Feb-19-2026 baselines vs post-overhaul cohorts.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setWeeks(r.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                weeks === r.value
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/30'
                  : 'text-[var(--isp-muted)] hover:bg-white/5 border border-transparent'
              }`}
              data-testid={`cohort-range-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-[var(--isp-muted)] gap-2 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Aggregating cohorts…
        </div>
      ) : !data || data.cohorts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-[var(--isp-muted)] gap-2">
          <BookOpen className="w-8 h-8 opacity-40" />
          <p className="text-sm">No signups in the last {weeks} weeks.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cohort-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.18em] text-[var(--isp-muted)] border-b border-[var(--isp-glass-border)]">
                <th className="text-left py-2 pr-3 font-semibold">Cohort</th>
                <th className="text-right py-2 px-2 font-semibold">Size</th>
                <th className="text-right py-2 px-2 font-semibold">D-1</th>
                <th className="text-right py-2 px-2 font-semibold">D-2</th>
                <th className="text-right py-2 px-2 font-semibold">D-7</th>
                <th className="text-right py-2 px-2 font-semibold">D-30</th>
              </tr>
            </thead>
            <tbody>
              {data.cohorts.map((c) => (
                <tr key={c.label} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-2 pr-3 font-mono text-xs text-white" title={`Week starting ${c.start_date}`}>
                    {c.label}
                    <span className="text-[10px] text-[var(--isp-muted)] ml-2 font-sans">
                      {c.start_date}
                    </span>
                  </td>
                  <td className="text-right py-2 px-2 text-white font-semibold">{c.cohort_size}</td>
                  <td className="text-right py-2 px-2"><Pct rate={c.rates.d1}  n={c.returns.d1}  /></td>
                  <td className="text-right py-2 px-2"><Pct rate={c.rates.d2}  n={c.returns.d2}  /></td>
                  <td className="text-right py-2 px-2"><Pct rate={c.rates.d7}  n={c.returns.d7}  /></td>
                  <td className="text-right py-2 px-2"><Pct rate={c.rates.d30} n={c.returns.d30} /></td>
                </tr>
              ))}
              <tr className="border-t-2 border-emerald-400/30 bg-emerald-500/5">
                <td className="py-3 pr-3 text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-bold">Average</td>
                <td className="py-3 px-2"></td>
                <td className="text-right py-3 px-2"><Pct rate={data.averages.d1}  n={''} /></td>
                <td className="text-right py-3 px-2"><Pct rate={data.averages.d2}  n={''} /></td>
                <td className="text-right py-3 px-2"><Pct rate={data.averages.d7}  n={''} /></td>
                <td className="text-right py-3 px-2"><Pct rate={data.averages.d30} n={''} /></td>
              </tr>
            </tbody>
          </table>
          <p className="text-[10px] text-[var(--isp-muted)] mt-3">
            Generated {data.generated_at?.slice(0, 16)?.replace('T', ' ')} UTC · cached 10 min
          </p>
        </div>
      )}
    </div>
  );
};

export default CohortRetentionTable;
